import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import ProductManagement from '../components/ProductManagement';
import FranchiseStockManagement from '../components/FranchiseStockManagement';
import FranchiseOrders from '../components/FranchiseOrders';
import FranchiseEarnings from '../components/FranchiseEarnings';
import LowStockNotifications from '../components/LowStockNotifications';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import CompletedOrders from '../components/CompletedOrders';
import CancelledOrders from '../components/CancelledOrders';
import ReturnReplaceRequests from '../components/ReturnReplaceRequests';
import AssignedPickups from './AssignedPickups';
import CompletedCancelledRequests from '../components/CompletedCancelledRequests';
import FranchiseCreateUser from '../components/FranchiseCreateUser';
import FranchiseOffersOptIn from '../components/FranchiseOffersOptIn';

const noop = () => {};

const FranchiseDashboard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [franchiseData, setFranchiseData] = useState(null);
    const [myFranchises, setMyFranchises] = useState([]);
    const [selectedFranchiseId, setSelectedFranchiseId] = useState('');
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('franchiseDetails');
    const [editingProfile, setEditingProfile] = useState(false);
    const [profileForm, setProfileForm] = useState({ name: '', address: '', city: '', state: '' });
    const [savingProfile, setSavingProfile] = useState(false);

    const locationStorageKey = user ? `selectedFranchiseId_${user.id}` : null;

    useEffect(() => {
        if (!user || user.role !== 'franchise') {
            navigate('/login');
            return;
        }
        fetchMyFranchises();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, navigate]);

    useEffect(() => {
        if (!user || user.role !== 'franchise') return;
        fetchFranchiseDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFranchiseId]);

    const fetchMyFranchises = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await apiClient.get('/franchise/my-franchises', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const list = response.data || [];
            setMyFranchises(list);

            const remembered = locationStorageKey ? localStorage.getItem(locationStorageKey) : null;
            const rememberedIsValid = remembered && list.some(f => f._id === remembered);
            setSelectedFranchiseId(rememberedIsValid ? remembered : (list[0]?._id || ''));
        } catch (error) {
            console.error('Error fetching franchise locations:', error);
            // Fall back to the single-location flow even if the list endpoint fails
            fetchFranchiseDetails();
        }
    };

    const handleLocationChange = (franchiseId) => {
        setSelectedFranchiseId(franchiseId);
        if (locationStorageKey) localStorage.setItem(locationStorageKey, franchiseId);
    };

    const fetchFranchiseDetails = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await apiClient.get('/franchise/my-franchise', {
                params: selectedFranchiseId ? { franchiseId: selectedFranchiseId } : {},
                headers: { Authorization: `Bearer ${token}` }
            });
            setFranchiseData(response.data);
            setProfileForm({
                name: response.data.name || '',
                address: response.data.location?.address || '',
                city: response.data.location?.city || '',
                state: response.data.location?.state || ''
            });
            setLoading(false);
        } catch (error) {
            console.error('Error fetching franchise data:', error);
            setLoading(false);
        }
    };

    const handleProfileSave = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            const token = localStorage.getItem('token');
            await apiClient.put('/franchise/my-franchise', { ...profileForm, franchiseId: selectedFranchiseId }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Franchise profile updated successfully!');
            setEditingProfile(false);
            fetchFranchiseDetails();
        } catch (error) {
            console.error('Error updating franchise profile:', error);
            alert(error.response?.data?.message || 'Failed to update profile.');
        } finally {
            setSavingProfile(false);
        }
    };

    if (loading) {
        return <div className="text-center mt-20 text-xl font-bold">Loading Franchise Dashboard...</div>;
    }

    return (
        <div className="dashboard-wrapper flex min-h-screen bg-gray-50">
            {/* Sidebar (Desktop) */}
            <aside className="w-64 bg-white shadow-lg hidden md:flex flex-col">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <h1 className="text-2xl font-extrabold text-green-700">Franchise Panel</h1>
                    <LowStockNotifications franchiseData={franchiseData} align="left" />
                </div>
                {myFranchises.length > 1 && (
                    <div className="px-4 pt-4">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Store Location</label>
                        <select
                            value={selectedFranchiseId}
                            onChange={(e) => handleLocationChange(e.target.value)}
                            className="w-full mt-1 p-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                        >
                            {myFranchises.map(f => (
                                <option key={f._id} value={f._id}>{f.name}</option>
                            ))}
                        </select>
                    </div>
                )}
                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                    {[
                        { id: 'franchiseDetails', label: 'Franchise Details' },
                        { id: 'products', label: 'Add/Manage Products' },
                        { id: 'inventory', label: 'Manage Inventory' },
                        { id: 'offers', label: 'Offers' },
                        { id: 'createUser', label: 'Create User' },
                        { id: 'orders', label: 'My Orders' },
                        { id: 'earnings', label: 'Earnings' },
                        { id: 'analytics', label: 'Analytics' },
                        { id: 'completedOrders', label: 'Completed' },
                        { id: 'cancelledOrders', label: 'Cancelled' },
                        { id: 'returnRequests', label: 'Return Requests' },
                        { id: 'pickupTasks', label: 'Pickup Tasks' },
                        { id: 'completedReturns', label: 'Completed Returns' },
                        { id: 'cancelledReturns', label: 'Cancelled Returns' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                                activeTab === tab.id
                                    ? 'bg-green-50 text-green-700 shadow-sm'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                    <div className="mt-8 pt-4 border-t border-gray-100 space-y-2">
                        <Link to="/" className="block w-full text-left px-4 py-3 rounded-lg font-medium text-blue-600 hover:bg-blue-50 transition-all duration-200">
                            View Site
                        </Link>
                        <button onClick={async () => { await logout(); navigate('/login'); }} className="w-full text-left px-4 py-3 rounded-lg font-medium text-red-600 hover:bg-red-50 transition-all duration-200">
                            Logout
                        </button>
                    </div>
                </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-4 md:p-8 overflow-y-auto">
                {/* Mobile Dropdown (Visible only on small screens) */}
                <div className="md:hidden mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h1 className="text-2xl font-bold">Franchise Dashboard</h1>
                        <LowStockNotifications franchiseData={franchiseData} />
                    </div>
                    {myFranchises.length > 1 && (
                        <div className="mb-4">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Store Location</label>
                            <select
                                value={selectedFranchiseId}
                                onChange={(e) => handleLocationChange(e.target.value)}
                                className="w-full mt-1 p-2.5 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-semibold text-gray-700 focus:ring-green-500 focus:border-green-500"
                            >
                                {myFranchises.map(f => (
                                    <option key={f._id} value={f._id}>{f.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <select
                        value={activeTab}
                        onChange={(e) => setActiveTab(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg shadow-sm bg-white focus:ring-green-500 focus:border-green-500"
                    >
                        <option value="franchiseDetails">Franchise Details</option>
                        <option value="products">Add/Manage Products</option>
                        <option value="inventory">Manage Inventory</option>
                        <option value="offers">Offers</option>
                        <option value="createUser">Create User</option>
                        <option value="orders">My Orders</option>
                        <option value="earnings">Earnings</option>
                        <option value="analytics">Analytics</option>
                        <option value="completedOrders">Completed</option>
                        <option value="cancelledOrders">Cancelled</option>
                        <option value="returnRequests">Return Requests</option>
                        <option value="pickupTasks">Pickup Tasks</option>
                        <option value="completedReturns">Completed Returns</option>
                        <option value="cancelledReturns">Cancelled Returns</option>
                    </select>
                </div>

                <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-100 min-h-[calc(100vh-8rem)]">
                    {activeTab === 'franchiseDetails' && (
                        <div>
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-800">Franchise Details</h2>
                                {franchiseData && !editingProfile && (
                                    <button
                                        onClick={() => setEditingProfile(true)}
                                        className="text-sm font-bold text-blue-600 hover:text-blue-800 px-4 py-2 border-2 border-blue-200 rounded-lg hover:bg-blue-50 transition-all"
                                    >
                                        Edit Profile
                                    </button>
                                )}
                            </div>
                            {franchiseData ? (
                                editingProfile ? (
                                    <form onSubmit={handleProfileSave} className="bg-gray-50 p-5 rounded-lg border border-gray-200 space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Franchise Name</label>
                                            <input type="text" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} className="w-full p-2.5 border rounded-lg" required />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Address</label>
                                            <input type="text" value={profileForm.address} onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">City</label>
                                                <input type="text" value={profileForm.city} onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">State</label>
                                                <input type="text" value={profileForm.state} onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-400 italic">Pincodes covered and commission rate are managed by the admin and can't be changed here.</p>
                                        <div className="flex gap-3">
                                            <button type="submit" disabled={savingProfile} className="bg-green-600 text-white font-bold px-6 py-2.5 rounded-lg hover:bg-green-700 disabled:opacity-60">
                                                {savingProfile ? 'Saving...' : 'Save Changes'}
                                            </button>
                                            <button type="button" onClick={() => setEditingProfile(false)} className="bg-gray-300 text-gray-700 font-bold px-6 py-2.5 rounded-lg hover:bg-gray-400">
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="bg-gray-100 p-5 rounded-lg border border-gray-200">
                                        <p className="mb-2"><strong>Franchise Name:</strong> {franchiseData.name}</p>
                                        <p className="mb-2"><strong>Status:</strong> <span className={franchiseData.status === 'active' ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{franchiseData.status.toUpperCase()}</span></p>
                                        <p className="mb-2"><strong>Commission Rate:</strong> {franchiseData.commissionRate}%</p>
                                        <p className="mb-2"><strong>Pincodes Covered:</strong> {franchiseData.pincodesCovered?.join(', ')}</p>
                                        <p className="mb-2"><strong>Location:</strong> {franchiseData.location?.address}, {franchiseData.location?.city}</p>
                                    </div>
                                )
                            ) : (
                                <p className="text-red-500">Your account does not seem to be linked to an active franchise.</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'products' && (
                        <div>
                            <h2 className="text-2xl font-bold mb-6 text-gray-800">Manage Global Products</h2>
                            <ProductManagement franchiseData={franchiseData} />
                        </div>
                    )}

                    {activeTab === 'inventory' && (
                        <div>
                            <h2 className="text-2xl font-bold mb-6 text-gray-800">Manage Local Inventory</h2>
                            <FranchiseStockManagement franchiseData={franchiseData} />
                        </div>
                    )}

                    {activeTab === 'offers' && (
                        <div>
                            <h2 className="text-2xl font-bold mb-6 text-gray-800">Offers</h2>
                            <FranchiseOffersOptIn franchiseId={selectedFranchiseId} />
                        </div>
                    )}

                    {activeTab === 'createUser' && <FranchiseCreateUser franchiseId={selectedFranchiseId} />}

                    {activeTab === 'orders' && (
                        <div>
                            <h2 className="text-2xl font-bold mb-6 text-gray-800">My Orders</h2>
                            <FranchiseOrders franchiseId={selectedFranchiseId} />
                        </div>
                    )}

                    {activeTab === 'earnings' && (
                        <div>
                            <h2 className="text-2xl font-bold mb-6 text-gray-800">Earnings</h2>
                            <FranchiseEarnings franchiseId={selectedFranchiseId} />
                        </div>
                    )}

                    {activeTab === 'analytics' && <AnalyticsDashboard franchiseData={franchiseData} />}
                    {activeTab === 'completedOrders' && <CompletedOrders />}
                    {activeTab === 'cancelledOrders' && <CancelledOrders />}
                    {activeTab === 'returnRequests' && (
                        <ReturnReplaceRequests deliveryPartners={[]} setActiveTab={noop} setRefreshFlag={noop} />
                    )}
                    {activeTab === 'pickupTasks' && <AssignedPickups setActiveTab={noop} />}
                    {activeTab === 'completedReturns' && <CompletedCancelledRequests type="completed" />}
                    {activeTab === 'cancelledReturns' && <CompletedCancelledRequests type="cancelled" />}
                </div>
            </main>
        </div>
    );
};

export default FranchiseDashboard;
